"use client";
import { fadeUp, stagger } from "@/app/lib/utils/motionVariants";
import { withBasePath, withMediaUrl } from "@/app/lib/basePath";
import { motion } from "framer-motion";
import { Mail, MessageCircle, Phone, Send, AlertCircle } from "lucide-react";
import Script from "next/script";
import { useEffect, useRef, useState } from "react";
import Image from "next/image";


interface FormField {
  label: string;
  slug: string;
  options?: Array<{ label: string; value: string }>;
}

interface ContactData {
  id: number;
  title: string;
  titleHighlight?: string;
  description: string;
  formFields: FormField[];
  buttonLabel: string;
  contactV2: Array<{ field: string; value: string }>;
}

type ContactProps = {
  contactData: ContactData | null;
  lang: string;
  turnstileSiteKey: string;
};

declare global {
  interface Window {
    turnstile?: {
      render: (container: HTMLElement, options: any) => string;
      remove: (widgetId: string) => void;
      reset: (widgetId?: string) => void;
    };
    contactTurnstileSuccess?: (token: string) => void;
    contactTurnstileExpired?: () => void;
  }
}

const createInitialFormData = (contactData: ContactData | null) => {
  const initialFormData: Record<string, string> = {};

  contactData?.formFields.forEach((field) => {
    initialFormData[field.slug] = "";
  });

  return initialFormData;
};

export default function ContactPage({ contactData: initialData, lang, turnstileSiteKey }: ContactProps) {
  const [submitted, setSubmitted] = useState(false);
  const contactData = initialData;
  const title = contactData?.title ?? "";
  const highlight = contactData?.titleHighlight?.trim();
  const highlightIndex = highlight
    ? title.toLowerCase().indexOf(highlight.toLowerCase())
    : -1;
  const [formData, setFormData] = useState<Record<string, string>>(() => createInitialFormData(initialData));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [turnstileToken, setTurnstileToken] = useState("");
  const widgetRef = useRef<string | null>(null);

  useEffect(() => {
    if (!turnstileSiteKey) return;

    const initialize = () => {
      if (!window.turnstile) {
        setTimeout(initialize, 200);
        return;
      }

      const container = document.getElementById("contact-turnstile");

      if (!container) return;

      container.innerHTML = "";

      const widgetId = window.turnstile.render(container, {
        sitekey: turnstileSiteKey,
        theme: "dark",
        callback: (token: string) => {
          setTurnstileToken(token);
          setErrors((prev) => ({ ...prev, turnstile: "" }));
        },
        "expired-callback": () => {
          setTurnstileToken("");
        },
        "error-callback": () => {
          setTurnstileToken("");
        },
      });

      widgetRef.current = widgetId;
    };

    initialize();

    return () => {
      if (widgetRef.current && window.turnstile) {
        window.turnstile.remove(widgetRef.current);
        widgetRef.current = null;
      }
    };
  }, [turnstileSiteKey]);

  useEffect(() => {
    window.contactTurnstileSuccess = (token: string) => {
      setTurnstileToken(token);
      setErrors((prev) => ({ ...prev, turnstile: "" }));
    };
    window.contactTurnstileExpired = () => setTurnstileToken("");

    return () => {
      delete window.contactTurnstileSuccess;
      delete window.contactTurnstileExpired;
    };
  }, []);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    contactData?.formFields.forEach((field) => {
      const value = formData[field.slug]?.trim();

      if (!value) {
        newErrors[field.slug] = `${field.label} is required`;
        return;
      }

      if (field.slug === "your-email") {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) {
          newErrors[field.slug] = "Please enter a valid email address";
        }
      }

      if (field.slug === "phone-number") {
        const phoneRegex = /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/;
        if (value && !phoneRegex.test(value)) {
          newErrors[field.slug] = "Please enter a valid phone number";
        }
      }
    });

    if (!turnstileSiteKey) {
      newErrors.turnstile = "Verification is not configured. Please try again later.";
    } else if (!turnstileToken) {
      newErrors.turnstile = "Please complete the verification.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError("");

    if (!validateForm()) return;

    setSubmitting(true);

    try {
      const payload = {
        lang,
        turnstileToken,
        formData,
        formFields: contactData?.formFields ?? [],
      };

      const response = await fetch(withBasePath("/mailservice/send-mail"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      let responseData: { message?: string; error?: string } = {};
      try {
        responseData = await response.json();
      } catch (parseError) {
        // console.error("[Contact Form] Failed to parse send-mail response", parseError);
      }

      if (!response.ok) {
        // console.error("[Contact Form] Send-mail request failed", {
        //   status: response.status,
        //   statusText: response.statusText,
        //   response: responseData,
        // });
        throw new Error(responseData.message || "Failed to submit form");
      }

      // // console.info("[Contact Form] Contact email sent successfully", responseData);
      setSubmitted(true);
      setFormData(createInitialFormData(contactData));
      setTurnstileToken("");
      if (widgetRef.current) {
        window.turnstile?.reset(widgetRef.current);
      }
      // window.turnstile?.reset();
    } catch (error) {
      // console.error("[Contact Form] Submit error:", error);
      setSubmitError(
        error instanceof Error ? error.message : "An error occurred while submitting the form"
      );
      setTurnstileToken("");
      if (widgetRef.current) {
        window.turnstile?.reset(widgetRef.current);
      }
      // window.turnstile?.reset();
    } finally {
      setSubmitting(false);
    }
  };

  if (!contactData) {
    return (
      <section className="relative min-lg:h-[50vh] flex flex-col items-center justify-center">
        <div className="text-center">Unable to load contact information</div>
      </section>
    );
  }

  const getContactImage = (slug: string, value: string): string | null => {
    const lower = slug.toLowerCase();
    const cleaned = value.replace(/\s+/g, "");

    if (lower.includes("email")) return "https://cdn-icons-png.flaticon.com/512/6806/6806987.png";
    if (lower.includes("whatsapp")) return "https://cdn-icons-png.flaticon.com/512/3670/3670051.png";
    if (cleaned.startsWith("+61")) return "https://cdn-icons-png.flaticon.com/512/323/323367.png";
    if (cleaned.startsWith("+1")) return "https://t4.ftcdn.net/jpg/06/16/12/61/360_F_616126121_mrm5owYNMcio51TsqSisoL6T4NfPHV95.jpg";
    if (cleaned.startsWith("+44")) return "https://cdn-icons-png.flaticon.com/512/8363/8363075.png";
    if (cleaned.startsWith("+91")) return "https://cdn-icons-png.flaticon.com/512/10597/10597864.png";

    return null;
  };

  const getContactLink = (label: string, value: string) => {
    const lowerLabel = label.toLowerCase();
    if (lowerLabel.includes("email")) return `mailto:${value}`;
    if (lowerLabel.includes("whatsapp")) return `https://wa.me/${value.replace(/\D/g, "")}`;
    return `tel:${value}`;
  };

  return (
    <>
      {/* <Script src="https://challenges.cloudflare.com/turnstile/v0/api.js" async defer /> */}
      <section className="relative min-lg:h-[50vh] flex flex-col items-center justify-center">
        <div className="-translate-x-1/2 -z-1 absolute aspect-square left-1/2 max-w-full lg:max-w-[2560px] md:aspect-auto md:min-h-[800px] pointer-events-none top-0 w-full">
          <div className="shadow-[0_0_120px_110px_rgb(36,91,187),0_0_220px_218px_rgb(36,91,187),0_0_790px_320px_rgb(36,91,187)] h-[0.1px] w-[0.1px] left-1/3 top-1/4 -translate-middle absolute rounded-full backdrop-blur-xl bg-[rgb(22,154,156)] scale-150 opacity-40"></div>
          <div className="absolute -bottom-40 left-0 w-full md:w-96 h-96 bg-primary/20 rounded-full blur-3xl" />
          <div className="absolute -bottom-40 right-0 w-full md:w-96 h-96 bg-primary/20 rounded-full blur-3xl" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full lg:w-[800px] h-[800px] bg-accent/10 rounded-full blur-3xl" />
        </div>
        <div className="container mx-auto relative z-10 pt-24 lg:pt-32 pb-4 md:pb-6 lg:pb-8">
          <motion.div initial="hidden" animate="visible" variants={stagger} className="max-w-4xl mx-auto text-center">
            <motion.h1 variants={fadeUp} className="banner-title">
              {highlightIndex >= 0 && highlight ? (
                <>
                  {title.slice(0, highlightIndex)}
                  <span className="banner-highlight-title">
                    {title.slice(highlightIndex, highlightIndex + highlight.length)}
                  </span>
                  {title.slice(highlightIndex + highlight.length)}
                </>
              ) : (
                title
              )}
            </motion.h1>
            <motion.p variants={fadeUp} className="mt-3 banner-description">
              {contactData?.description}
            </motion.p>
          </motion.div>
        </div>
      </section>

      <section className="py-4 md:py-6 lg:py-8">
        <div className="container mx-auto">
          <div className="grid lg:grid-cols-2 gap-6 lg:gap-8 max-w-5xl mx-auto">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
              {submitted ? (
                <div className="glass-card p-12 text-center">
                  <Send className="w-12 h-12 text-primary mx-auto mb-4" />
                  <h3 className="text-2xl font-bold mb-2">Message Sent!</h3>
                  <p className="text-muted-foreground">We&apos;ll get back to you soon.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="glass-card p-4 lg:p-6 space-y-5 !bg-[#07122E]/50 relative">
                  {submitError && (
                    <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-4 flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                      <p className="text-red-500 text-sm">{submitError}</p>
                    </div>
                  )}

                  {contactData?.formFields?.map((field) => (
                    <div key={field.slug}>
                      <label className="block text-sm font-medium mb-2">
                        {field.label}
                        {!field.slug.includes("phone") && <span className="text-red-500 ml-1">*</span>}
                      </label>
                      {field.options ? (
                        <div className="relative">
                          <select
                            name={field.slug}
                            value={formData[field.slug] || ""}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 rounded-xl border border-white/10 bg-white/[0.05] text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all appearance-none" >
                            <option value="">Select an option</option>
                            {field.options.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                          {/* Custom Arrow */}
                          <svg
                            className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 9l-7 7-7-7"
                            />
                          </svg>
                        </div>
                      ) : field.slug === "your_message" ? (
                        <textarea
                          name={field.slug}
                          value={formData[field.slug] || ""}
                          onChange={handleInputChange}
                          rows={4}
                          className="w-full px-4 py-3 rounded-xl border border-white/10 bg-white/[0.05] text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all resize-none"
                        />
                      ) : (
                        <input
                          type={field.slug === "your-email" ? "email" : field.slug === "phone-number" ? "tel" : "text"}
                          name={field.slug}
                          value={formData[field.slug] || ""}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 rounded-xl border border-white/10 bg-white/[0.05] text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all"
                        />
                      )}
                      {errors[field.slug] && (
                        <p className="text-red-500 text-sm mt-2">{errors[field.slug]}</p>
                      )}
                    </div>
                  ))}

                  <div className="mt-4">
                    {turnstileSiteKey ? (
                      // <div
                      //   className="cf-turnstile"
                      //   data-sitekey={turnstileSiteKey}
                      //   data-theme="dark"
                      //   data-callback="contactTurnstileSuccess"
                      //   data-expired-callback="contactTurnstileExpired"
                      //   data-error-callback="contactTurnstileExpired"
                      // />
                      <div id="contact-turnstile" />
                    ) : (
                      <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-4 text-red-500 text-sm">
                        Verification is not configured. Please contact the site administrator.
                      </div>
                    )}
                    {errors.turnstile && (
                      <p className="text-red-500 text-sm mt-2">{errors.turnstile}</p>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    className="btn-primary w-full text-center flex justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {submitting ? "Sending..." : contactData?.buttonLabel}
                  </button>
                </form>
              )}
            </motion.div>





            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="space-y-3">
              {contactData?.contactV2?.map((detail:any) => {
                const lowerField = detail.field.toLowerCase();
                const link = getContactLink(detail.field, detail.value);
                const image = getContactImage(detail.field, detail.value);
                const FallbackIcon = lowerField.includes("email")
                  ? Mail
                  : lowerField.includes("whatsapp")
                    ? MessageCircle
                    : lowerField.includes("address")
                      ? Phone
                      : Phone;

                return (
                  <motion.div
                    variants={fadeUp}
                    key={detail.id ?? detail.field}
                    className="relative bg-gradient-to-r from-primary via-secondary to-[#8B63FF] shadow-[0_0_35px_rgba(62,140,255,0.35)] rounded-2xl p-[1px] overflow-hidden hover:!-translate-y-1 transition-all duration-500"
                  >
                    <a
                      key={detail.field}
                      href={link}
                      title={detail.field}
                      target={detail.field.toLowerCase().includes("whatsapp") ? "_blank" : undefined}
                      rel={detail.field.toLowerCase().includes("whatsapp") ? "noopener noreferrer" : undefined}
                      className=" bg-[#07122E] px-4 py-3 flex items-center gap-3 hover:shadow-[0_0_30px_rgba(30,58,138,0.15)] transition-all group relative z-[1] rounded-2xl"
                    >
                      {/* Top Highlight */}
                      <div className="absolute top-0 left-8 right-8 h-px bg-gradient-to-r from-transparent via-primary to-transparent"></div>

                      {/* Left Glow */}
                      <div className="absolute -left-28 top-1/2 -translate-y-1/2 h-full w-[112px] rounded-full  bg-[#01e3ff] blur-[5px] z-[-1]" />

                      {/* Right Glow */}
                      <div className="absolute -right-24 top-0 -translate-y-1/2 h-full w-[100px] rounded-full bg-violet-500 blur-[5px] z-[-1]" />
                      <div className="absolute -left-28 top-1/2 -translate-y-1/2 h-full w-60 rounded-full  bg-cyan-400/15 blur-[30px] z-[-1]" />

                      {/* Right Glow */}
                      <div className="absolute -right-24 top-1/2 -translate-y-1/2 h-full w-60 rounded-full bg-violet-500/15 blur-[50px] z-[-1]" />

                      <div className="w-10 h-10 rounded-full overflow-hidden flex items-center justify-center transition-transform ">
                        {image ? (
                          <Image src={detail.flag?.url ? withMediaUrl(detail.flag?.url) : image } width={40} height={40}
                            alt={detail.flag?.alternativeText || "Cover Image"}
                            className="w-full h-full rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full rounded-full gradient-primary flex items-center justify-center">
                            <FallbackIcon className="w-6 h-6 text-white" />
                          </div>
                        )}
                      </div>
                      <div>
                        <div className="font-semibold capitalize">{detail.field}</div>
                        <div className="text-muted-foreground">{detail.value}</div>
                      </div>
                    </a>
                  </motion.div>
                );
              })}
            </motion.div>
          </div>

        </div>
      </section>
    </>
  );
}

